import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    privacyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Anfrage erfolgreich gesendet!",
          description: response.data.message,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          message: '',
          privacyAccepted: false
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-atob-blue via-atob-blue to-gray-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-atob-orange font-semibold text-sm uppercase tracking-wide mb-4 block">
            Kontakt
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Kostenlose Erstberatung anfragen
          </h2>
          <p className="text-lg text-gray-200">
            Ein Anruf, eine E-Mail, ein Formular – wir melden uns innerhalb von 24 Stunden
            mit einer ehrlichen Ersteinschätzung.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-atob-blue font-semibold">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Max Mustermann"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-atob-blue font-semibold">
                      E-Mail *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="max@beispiel.de"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-atob-blue font-semibold">
                      Telefon *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="+49 123 4567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="serviceType" className="text-atob-blue font-semibold">
                      Art der Dienstleistung
                    </Label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-atob-orange"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="kauf-verkauf">Kauf & Verkauf</option>
                      <option value="finanzierung">Finanzierung</option>
                      <option value="handwerk">Sanierung & Handwerk</option>
                      <option value="facility">Facility & Instandhaltung</option>
                      <option value="portfolio">Portfolio-Beratung / Anleger</option>
                      <option value="sonstiges">Sonstiges / Erstberatung</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-atob-blue font-semibold">
                    Ihre Nachricht *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    rows={6}
                    placeholder="Beschreiben Sie bitte kurz Ihr Anliegen..."
                  />
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start space-x-3 p-4 bg-gray-100 rounded-lg">
                  <Checkbox
                    id="privacyAccepted"
                    name="privacyAccepted"
                    checked={formData.privacyAccepted}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({ ...prev, privacyAccepted: checked }));
                    }}
                    required
                    className="mt-1"
                  />
                  <label htmlFor="privacyAccepted" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    Ich habe die{' '}
                    <a href="/datenschutz" className="text-atob-orange hover:underline font-medium" target="_blank">
                      Datenschutzerklärung
                    </a>{' '}
                    gelesen und bin einverstanden, dass meine Daten zur Kontaktaufnahme verarbeitet werden. *
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.privacyAccepted}
                  className="w-full bg-atob-orange hover:bg-atob-orange/90 text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Anfrage senden
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-atob-orange to-yellow-600 rounded-2xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-atob-blue">Kontaktinformationen</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-atob-blue" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1 text-atob-blue">Büro (Festnetz)</p>
                    <a href={`tel:${companyInfo.phone_office.replace(/\s/g, '')}`} className="text-atob-blue hover:text-white transition-colors font-semibold block" data-testid="contact-office-phone">{companyInfo.phone_office}</a>
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-atob-blue hover:text-white transition-colors font-semibold block mt-2" data-testid="contact-mobile-phone">Mobil: {companyInfo.phone}</a>
                    <p className="text-sm text-atob-blue/80 mt-1">Mo-Fr: 8:00 - 18:00 Uhr</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-atob-blue" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1 text-atob-blue">E-Mail</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-atob-blue hover:text-white transition-colors break-words">{companyInfo.email}</a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-atob-blue" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1 text-atob-blue">Adresse</p>
                    <p className="text-atob-blue">{companyInfo.address.street}</p>
                    <p className="text-atob-blue">{companyInfo.address.city}</p>
                    <p className="text-atob-blue">{companyInfo.address.country}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Info Card */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-2 border-atob-orange shadow-lg">
              <h4 className="font-bold text-atob-orange mb-3 text-lg">Wie es weitergeht</h4>
              <ol className="text-atob-blue text-sm leading-relaxed space-y-2 list-decimal list-inside">
                <li>Rückmeldung innerhalb von 24 Stunden werktags</li>
                <li>Kurzes Erstgespräch – Telefon oder vor Ort</li>
                <li>Konkreter Vorschlag mit passenden Partnern aus dem Netzwerk</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
